import { type MatterTeamFormValues } from "@/components/features/settings/defaults/matter-teams/matterTeamFormSchema";
import {
  Button,
  SearchableSelectFormField,
  SelectFormField,
  Typography,
} from "@/components/ui";
import { useUserDirectory } from "@/hooks/use-user-directory";
import { type MatterRole } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import { Plus, Trash2 } from "lucide-react";
import { useFieldArray, type UseFormReturn } from "react-hook-form";

const newTeammateRowId = (): string => {
  if (typeof globalThis.crypto.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

interface MatterTeammatesInlineFieldsProps {
  parentForm: UseFormReturn<MatterTeamFormValues>;
  matterRoles: MatterRole[];
  enabled: boolean;
}

function TeammateUserField({
  parentForm,
  index,
  enabled,
}: {
  parentForm: UseFormReturn<MatterTeamFormValues>;
  index: number;
  enabled: boolean;
}) {
  const userDirectory = useUserDirectory({
    enabled,
    selectedIds: [parentForm.watch(`newMatterTeammates.${index}.id`)],
  });
  return (
    <SearchableSelectFormField
      className="w-full min-w-0 !space-y-0"
      buttonClassName="h-10 py-2"
      items={userDirectory.users}
      itemsKey="id"
      formatItemLabel={(item) => formatUsersName(item) ?? ""}
      register={parentForm.register(`newMatterTeammates.${index}.id`)}
      placeholder="Select an assignee"
      {...userDirectory.selectProps}
    />
  );
}

export function MatterTeammatesInlineFields({
  parentForm,
  matterRoles,
  enabled,
}: MatterTeammatesInlineFieldsProps) {
  const { control, register } = parentForm;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "newMatterTeammates",
  });

  const addRow = () => {
    append({
      matterTeammateId: newTeammateRowId(),
      id: "",
      matterRoleId: "",
    });
  };

  return (
    <div className="col-span-full">
      {fields.length === 0 ? (
        <Typography
          variant="subHeading"
          className="text-muted-foreground mb-2 italic"
        >
          No team members added.
        </Typography>
      ) : (
        <ul className="space-y-2">
          {fields.map((field, index) => (
            <li
              key={field.id}
              className="flex flex-col gap-1"
              data-testid={
                field.matterTeammateId
                  ? `matter-teammate-item-${field.matterTeammateId}`
                  : undefined
              }
            >
              <div className="flex min-w-0 items-stretch gap-2 sm:gap-3">
                <div className="flex min-w-0 flex-1 flex-col justify-center">
                  <TeammateUserField
                    parentForm={parentForm}
                    index={index}
                    enabled={enabled}
                  />
                </div>
                <div className="flex w-36 min-w-0 flex-col justify-center self-stretch sm:w-40 [&>div]:!m-0 [&>div]:!space-y-0">
                  <SelectFormField<MatterRole>
                    className="h-10 w-full min-w-0"
                    placeholder="Select a role"
                    items={matterRoles}
                    itemsKey="id"
                    formatItemLabel={(item) => item?.label}
                    register={register(
                      `newMatterTeammates.${index}.matterRoleId`,
                    )}
                  />
                </div>
                <div className="flex shrink-0 flex-col justify-center self-stretch">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    onClick={() => {
                      remove(index);
                    }}
                    aria-label="Remove team member"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 w-full self-start">
        <Button
          type="button"
          variant="link"
          className="text-muted-foreground h-auto min-h-0 justify-start p-0"
          onClick={addRow}
          data-testid="add-team-member-button"
        >
          <Plus className="h-4 w-4 shrink-0" />
          Add team member
        </Button>
      </div>
    </div>
  );
}
